import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeForRole } from "@/context/AuthContext";
import RequireAuth from "@/components/RequireAuth";
import DashboardShell from "@/components/DashboardShell";
import MobileShell from "@/components/MobileShell";
import { Loading } from "@/components/kit";

import Login from "@/pages/Login";
import Dashboard from "@/pages/owner/Dashboard";
import DataHealth from "@/pages/owner/DataHealth";
import Team from "@/pages/owner/Team";
import Settings from "@/pages/owner/Settings";
import RewardsPromos from "@/pages/owner/RewardsPromos";
import Ledger from "@/pages/shared/Ledger";
import Record from "@/pages/shared/Record";
import Members from "@/pages/shared/Members";
import CustomerProfile from "@/pages/shared/CustomerProfile";
import Classes from "@/pages/shared/Classes";
import CheckIn from "@/pages/shared/CheckIn";
import Memberships from "@/pages/shared/Memberships";
import Outstanding from "@/pages/shared/Outstanding";
import PendingPayments from "@/pages/shared/PendingPayments";
import ScanMember from "@/pages/shared/ScanMember";
import Notifications from "@/pages/shared/Notifications";

import MemberHome from "@/pages/member/Home";
import MemberBooking from "@/pages/member/Booking";
import MemberRewards from "@/pages/member/Rewards";
import MemberCard from "@/pages/member/Card";
import MemberProfile from "@/pages/member/Profile";

import CoachClasses from "@/pages/coach/Classes";
import CoachHistory from "@/pages/coach/History";
import CoachProfile from "@/pages/coach/Profile";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-black"><Loading /></div>;
  return <Navigate to={user ? homeForRole(user.role) : "/login"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster theme="dark" position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />

          {/* OWNER */}
          <Route path="/owner" element={<RequireAuth roles={["owner"]}><DashboardShell role="owner" /></RequireAuth>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="ledger" element={<Ledger />} />
            <Route path="record" element={<Record />} />
            <Route path="scan-member" element={<ScanMember />} />
            <Route path="members" element={<Members />} />
            <Route path="members/:id" element={<CustomerProfile />} />
            <Route path="classes" element={<Classes />} />
            <Route path="checkin" element={<CheckIn />} />
            <Route path="memberships" element={<Memberships />} />
            <Route path="outstanding" element={<Outstanding />} />
            <Route path="pending-payments" element={<PendingPayments />} />
            <Route path="rewards" element={<RewardsPromos />} />
            <Route path="team" element={<Team />} />
            <Route path="data-health" element={<DataHealth />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* ADMIN */}
          <Route path="/admin" element={<RequireAuth roles={["admin"]}><DashboardShell role="admin" /></RequireAuth>}>
            <Route index element={<Navigate to="ledger" replace />} />
            <Route path="ledger" element={<Ledger />} />
            <Route path="record" element={<Record />} />
            <Route path="scan-member" element={<ScanMember />} />
            <Route path="checkin" element={<CheckIn />} />
            <Route path="classes" element={<Classes />} />
            <Route path="members" element={<Members />} />
            <Route path="members/:id" element={<CustomerProfile />} />
            <Route path="memberships" element={<Memberships />} />
            <Route path="outstanding" element={<Outstanding />} />
            <Route path="pending-payments" element={<PendingPayments />} />
            <Route path="rewards" element={<RewardsPromos />} />
            <Route path="team" element={<Team />} />
            <Route path="data-health" element={<DataHealth />} />
            <Route path="settings" element={<Settings />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>

          {/* COACH */}
          <Route path="/coach" element={<RequireAuth roles={["coach"]}><MobileShell role="coach" /></RequireAuth>}>
            <Route index element={<Navigate to="classes" replace />} />
            <Route path="classes" element={<CoachClasses />} />
            <Route path="history" element={<CoachHistory />} />
            <Route path="profile" element={<CoachProfile />} />
          </Route>

          {/* MEMBER */}
          <Route path="/member" element={<RequireAuth roles={["user"]}><MobileShell role="member" /></RequireAuth>}>
            <Route index element={<Navigate to="home" replace />} />
            <Route path="home" element={<MemberHome />} />
            <Route path="booking" element={<MemberBooking />} />
            <Route path="rewards" element={<MemberRewards />} />
            <Route path="card" element={<MemberCard />} />
            <Route path="profile" element={<MemberProfile />} />
          </Route>

          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
